import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CreateDemoForm } from '../components/admin/CreateDemoForm'
import { DemoCard } from '../components/admin/DemoCard'
import { DemoSearchBar } from '../components/admin/DemoSearchBar'
import { EditDemoForm } from '../components/admin/EditDemoForm'
import { type RawDemoData } from '../components/admin/types'
import { addRecentDemo, getRecentDemos, mapDemo } from '../components/admin/utils'
import { LoggedOutRedirect } from '../components/LoggedOutRedirect'
import { Modal } from '../components/Modal'
import { useAuth, useLogOut } from '../contexts/AuthContext'
import { type Demo } from '../Root'
import { Loading } from './Loading'

export { type DateRangeDatePickerModes } from '../components/admin/types'

export const Admin = () => {
  const [demos, setDemos] = useState<Demo[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [recentDemos, setRecentDemos] = useState<string[]>(getRecentDemos)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedDemo, setSelectedDemo] = useState<Demo | null>(null)
  const { isAdmin, authFetch } = useAuth()
  const logOut = useLogOut()
  const navigate = useNavigate()

  const handleDemoClick = useCallback((clientName: string) => {
    addRecentDemo(clientName)
    setRecentDemos(getRecentDemos())
  }, [])

  const handleOpenCreateModal = useCallback(() => {
    setIsCreateModalOpen(true)
  }, [])

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setSelectedDemo(null)
  }, [])

  const handleSelectRecent = useCallback(
    (clientName: string) => {
      const demo = demos.find(d => d.clientName === clientName)
      if (demo) {
        addRecentDemo(clientName)
        setRecentDemos(getRecentDemos())
        navigate(`/${clientName}`)
      }
    },
    [demos, navigate],
  )

  const filteredDemos = demos.filter(demo =>
    (showArchived || !demo.archivedAt)
    && demo.clientName.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const fetchDemos = useCallback(async () => {
    try {
      const response = await authFetch('/api/demos')
      if (!response.ok) {
        throw new Error('Failed to fetch')
      }
      const rawData = await response.json()
      const formattedData: Demo[] = rawData.map((data: RawDemoData) => mapDemo(data))
      setDemos(formattedData)
      setSelectedDemo(currentSelectedDemo => {
        if (!currentSelectedDemo) {
          return currentSelectedDemo
        }

        return (
          formattedData.find(
            demo => demo.clientName === currentSelectedDemo.clientName,
          ) ?? null
        )
      })
      setLoading(false)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching demos:', error)
      setLoading(false)
    }
  }, [authFetch])

  const handleCreateDemoCreated = useCallback(async () => {
    await fetchDemos()
    handleCloseCreateModal()
  }, [fetchDemos, handleCloseCreateModal])

  useEffect(() => {
    if (isAdmin) {
      void fetchDemos()
    }
  }, [fetchDemos, isAdmin])

  if (!isAdmin) {
    return <LoggedOutRedirect />
  }

  if (loading) {
    return <Loading />
  }

  return (
    <div>
      <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <button
          onClick={logOut}
          style={{
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            backgroundColor: '#fff',
            color: '#000',
            border: '2px solid #000',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
        >
          Log out
        </button>
      </div>
      <div style={{ textAlign: 'center', paddingTop: '20px' }}>
        <h1>Admin Dashboard</h1>
      </div>
      <DemoSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        recentDemos={recentDemos}
        onSelectRecent={handleSelectRecent}
        onCreateClick={handleOpenCreateModal}
        showArchived={showArchived}
        onShowArchivedChange={setShowArchived}
      />
      <Modal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal}>
        <CreateDemoForm
          onCreated={handleCreateDemoCreated}
          onClose={handleCloseCreateModal}
        />
      </Modal>
      <Modal isOpen={selectedDemo !== null} onClose={handleCloseEditModal}>
        {selectedDemo && (
          <EditDemoForm
            demo={selectedDemo}
            onCancel={handleCloseEditModal}
            onSaved={fetchDemos}
          />
        )}
      </Modal>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        {filteredDemos.map(demo => (
          <DemoCard
            key={demo.id}
            demo={demo}
            onDemoClick={handleDemoClick}
            onEdit={setSelectedDemo}
            onUpdated={fetchDemos}
          />
        ))}
      </div>
    </div>
  )
}
